import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { toast } from "react-toastify";
import { syndicationFeedApi, SyndicationFeed } from "api/syndicationFeed";
import { isOverAffordableHousingComCap } from "./affordableHousingComCap";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";

import { apartmentsVariantForKind } from "./apartmentsVariant";
import { ADD_LISTINGS_LABEL } from "./constants";
import { getFeedErrorMessage } from "./feedErrorMessage";
import { ListingSelectionTree } from "./ListingSelectionTree";
import {
  PickerUnit,
  fetchAllPages,
  fetchAllPickerUnits,
  isListableUnit
} from "./pickerUnits";
import {
  feedListingsQueryKey,
  invalidateFeedQueries
} from "./useSyndicationFeed";

interface AddListingsModalProps {
  feed: SyndicationFeed;
  feedLabel: string;
  opened: boolean;
  onClose: () => void;
}

const FEED_LISTINGS_PER_PAGE = 500;
const ADD_CONCURRENCY = 6;

const chunk = <T,>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, index * size + size)
  );

const settleInBatches = (
  units: PickerUnit[],
  worker: (unit: PickerUnit) => Promise<unknown>,
  size: number
): Promise<PromiseSettledResult<unknown>[]> =>
  chunk(units, size).reduce<Promise<PromiseSettledResult<unknown>[]>>(
    async (accPromise, batch) => [
      ...(await accPromise),
      ...(await Promise.allSettled(batch.map(worker)))
    ],
    Promise.resolve([])
  );

export const AddListingsModal = ({
  feed,
  feedLabel,
  opened,
  onClose
}: AddListingsModalProps) => {
  const queryClient = useQueryClient();
  const [selectedUnits, setSelectedUnits] = useState<Map<number, PickerUnit>>(
    new Map()
  );

  const { data: units, isLoading: isLoadingUnits } = useQuery({
    queryKey: ["ADD_LISTINGS_UNITS"],
    queryFn: fetchAllPickerUnits,
    enabled: opened
  });

  const { data: inFeedListings } = useQuery({
    queryKey: [...feedListingsQueryKey(feed), "IN_FEED_IDS"],
    // Every page matters here: a listing this misses is offered again as if it
    // weren't already in the feed.
    queryFn: () =>
      fetchAllPages((page) =>
        syndicationFeedApi.getFeedListings(feed, {
          page,
          per_page: FEED_LISTINGS_PER_PAGE
        })
      ),
    enabled: opened
  });

  const inFeedUnitIds = useMemo(
    () =>
      new Set(
        (inFeedListings ?? [])
          .map((listing) => listing.unit?.id)
          .filter((id): id is number => typeof id === "number")
      ),
    [inFeedListings]
  );

  const isEligible = useCallback(
    (unit: PickerUnit) =>
      isListableUnit(unit) &&
      !inFeedUnitIds.has(unit.id) &&
      !(
        feed === "affordable_housing_com" &&
        isOverAffordableHousingComCap(unit.monthly_rent_amount?.cents)
      ),
    [feed, inFeedUnitIds]
  );

  const handleClose = () => {
    setSelectedUnits(new Map());
    onClose();
  };

  // Kept stable so the tree's memoised rows don't all re-render on every toggle.
  const isSelected = useCallback(
    (unit: PickerUnit) => selectedUnits.has(unit.id),
    [selectedUnits]
  );

  const toggleUnit = useCallback(
    (unit: PickerUnit) =>
      setSelectedUnits((prev) => {
        const next = new Map(prev);
        next.has(unit.id) ? next.delete(unit.id) : next.set(unit.id, unit);
        return next;
      }),
    []
  );

  const setUnitsSelected = useCallback(
    (unitsToSet: PickerUnit[], select: boolean) =>
      setSelectedUnits((prev) => {
        const next = new Map(prev);
        unitsToSet.forEach((unit) =>
          select ? next.set(unit.id, unit) : next.delete(unit.id)
        );
        return next;
      }),
    []
  );

  const addMutation = useMutation({
    mutationKey: [...feedListingsQueryKey(feed), "ADD"],
    mutationFn: (unitsToAdd: PickerUnit[]) =>
      settleInBatches(
        unitsToAdd,
        (unit) =>
          syndicationFeedApi.toggleFeedListing(feed, unit.id, {
            enabled: true,
            variant:
              feed === "apartments_com"
                ? apartmentsVariantForKind(unit.property?.kind)
                : undefined
          }),
        ADD_CONCURRENCY
      ),
    onSuccess: (results, addedUnits) => {
      invalidateFeedQueries(queryClient, feed);

      const succeededIds = addedUnits
        .filter((_, index) => results[index]?.status === "fulfilled")
        .map((unit) => unit.id);
      const failures = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );

      if (succeededIds.length > 0)
        toast.success(
          `${succeededIds.length} listing${succeededIds.length > 1 ? "s" : ""} added to the ${feedLabel} feed`
        );

      if (failures.length > 0) {
        setSelectedUnits((prev) => {
          const next = new Map(prev);
          succeededIds.forEach((id) => next.delete(id));
          return next;
        });
        toast.error(
          `${failures.length} couldn't be added: ${getFeedErrorMessage(failures[0].reason)}`
        );
        return;
      }

      handleClose();
    },
    onError: (error) => toast.error(getFeedErrorMessage(error))
  });

  const selectedCount = selectedUnits.size;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      panelClassName="m-4 flex max-h-[85vh] w-full max-w-[640px] flex-col"
    >
      <header className="flex items-center justify-between p-6 pb-4">
        <h2 className="text-xl font-bold text-content-1000">
          {ADD_LISTINGS_LABEL}
        </h2>
        <IconButton
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.LG}
          HugeIcon={Cancel01Icon}
          onClick={handleClose}
        />
      </header>

      <ListingSelectionTree
        units={units ?? []}
        isEligible={isEligible}
        isSelected={isSelected}
        onToggleUnit={toggleUnit}
        onToggleMany={setUnitsSelected}
        isLoading={isLoadingUnits}
      />

      <footer className="flex items-center justify-end gap-2 border-t border-alpha-40 px-6 py-4">
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          loading={addMutation.isPending}
          disabled={selectedCount === 0 || addMutation.isPending}
          onClick={() => addMutation.mutate([...selectedUnits.values()])}
        >
          {ADD_LISTINGS_LABEL}
        </Button>
      </footer>
    </Modal>
  );
};
